import 'core-js/stable';
import 'regenerator-runtime/runtime';
import '@stardazed/streams-polyfill';
import 'app/agriculture/styles/index.scss';
import {
    HTTP_INTERCEPTOR_FACTORY_NAME,
    standardApplicationBootstrapping,
    withHTTPInterceptor,
} from 'app/index_utils';

import { AgricultureRestrictionsService } from 'app/licensing/AgricultureRestrictionsService';
import { LocationProcessor } from 'app/shared/collections/time/processor/location_processor.factory'; // polyfill for web streams
import { HTMLDomDocumentService } from 'app/shared/html_dom_document_service';
import { CensoredSystem, IMS_TYPE__CENSORED_SYSTEM } from 'app/shared/collections/censored_system';
import { EventRegistry } from 'app/shared/events/event_registry';
import { ApiSyncService } from './shared/api_sync/ApiSyncService';
import angular, {
    ICompileService,
    IFilterService,
    IHttpService,
    IIntervalService,
    ILocationService,
    IQService,
    IRootScopeService,
    ITemplateCacheService,
    ITemplateRequestService,
    ITimeoutService,
    IWindowService,
} from 'angular';
import localStorageModule, { ICookie } from 'angular-local-storage';
import ngResourceModule from 'angular-resource';
import ngCookiesModule from 'angular-cookies';
import ngStrapModule from 'angular-strap';
import ngUIBootstrapModule from 'angular-ui-bootstrap';
import ngUIBootstrapDateTimePicker from 'angularjs-bootstrap-datetimepicker';
import ngSanitizeModule from 'angular-sanitize';
import 'angular-ivh-treeview';
import { Cesium } from './cesium';
import { setupAngularImports } from './shared/angular_imports';
import { setupResources } from './shared/resources';
import { ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import { Asset, IMS_TYPE__ASSET } from './shared/collections/asset';
import { AssetType, IMS_TYPE__ASSET_TYPE } from 'app/shared/collections/asset_type';
import { IMS_TYPE__INTEGRATION, Integration } from 'app/shared/collections/integration';
import {
    IMS_TYPE__LOCATION_STAT,
    locationStatDefinition,
    LocationStats,
} from 'app/shared/collections/location_stat';
import { IMS_TYPE__TAG, Tag } from 'app/shared/collections/tag';
import { IMS_TYPE__VIEW, View } from 'app/shared/collections/view';
import { IMS_TYPE__ZONE_HISTORY, ZoneHistory } from './shared/collections/zones/zone_history';
import { IMS_TYPE__ZONE_TYPE, ZoneType } from './shared/collections/zones/zone_type';
import { ConfigService } from './config/config.service';
import { trustedFilter } from './shared/filters/trusted.filter';
import { compassDragRingDirective } from 'app/map/components/compass/compass.component';
import { Compass } from 'app/map/components/compass/compass';
import { ScaleIndicator } from 'app/agriculture/AgricultureMap/components/ScaleIndicator/ScaleIndicator.service';
import { ImsIconUrlLookup } from './shared/icons/ims_icon_url_lookup';
import {
    uibTabDirectiveDecorator,
    uibTabWithCountDirective,
} from './shared/components/tab_count/ims_tab_count.component';
import { ImsPanelService } from './shared/ims_panel.service';
import { MainService } from './main/MainService';
import TagCollection from './shared/collections/tag_collection.service';
import { AssetCollection } from './shared/collections/asset_collection.service';
import { ZoneCollection } from './shared/collections/zones/zone_collection.service';
import ZoneTypeCollection from './shared/collections/zones/zone_type_collection.service';
import { ZoneHistoryCollection } from './shared/collections/zones/zone_history_collection.service';
import { ColorRendererService } from './shared/color_renderer.service';
import { MapService } from './map/services/map.service';
import { ImsImageryLayersService } from './map/services/ImsImageryLayersService';
import { TerrainService } from './map/services/terrain.service';
import { UrlInitParams } from './main/url_init_params.service';
import { Cartesian3Util } from './shared/utilities/cartesian3_util.service';
import { AssetTypeCollection } from './shared/collections/asset_type_collection.service';
import { ImageUrlService } from './shared/image_url.service';
import { ViewStateService } from './shared/view_state.service';
import { TitleService } from './main/title.service';
import { DashboardItemService } from './overview/dashboard/dashboard_item.service';
import { ClockHandler } from './shared/time/clock_handler.service';
import { ClockHandlerFSM } from './shared/time/clock_handler_fsm';
import { ServerTimeOffsetService } from './shared/time/server_time_offset.service';
import { KeypressService } from './shared/keypress.service';
import { MapSitesService } from './map/services/map_sites.service';
import { ViewCollection } from './shared/collections/view_collection.service';
import { BaseImageryLayersService } from './shared/BaseImageryLayersService';
import { autoResizeSelectDirective } from './shared/directives/auto-resize-select.directive';
import { assetNextHopTreeDirective } from './overview/logical_views/asset_next_hop_tree/asset_next_hop_tree.directive';
import { LocationStatCollection } from './shared/collections/location_stat_collection.service';
import { ngRightClickDirective } from './shared/directives/right_click.directive';
import { emptyToNullDirective } from './shared/directives/empty_to_null.directive';
import { IntegrationCollection } from './shared/collections/integration_collection.service';
import { titleUpdaterDirective } from './main/title_updater.directive';
import { onSizeChangedDirective } from './shared/utilities/on_size_changed.directive';
import { addSharedComponents } from './shared/components/builder';
import { SelectionService } from './shared/selection/SelectionService';
import { MapTooltipService } from './map/services/map_tooltip.service';
import { ImsItemPanelService } from 'app/shared/ims_items/ImsItemPanelService';
import { HelpRegistry } from 'app/shared/help_and_information/HelpRegistry';
import { assetHelpDetails, createAssetTour } from 'app/items/assets/asset_help';
import { ImsObjectVisibilityService } from 'app/shared/visibility/ImsObjectVisibilityService';
import { createIpDeviceTour } from 'app/items/ip_devices/ip_device_help';
import {
    dashboardDataSourceSettingsHelp,
    dashboardItemHelp,
    dashboardLegendHelp,
    manageDashboardTabHelpDetails,
} from 'app/overview/dashboard/dashboard_help';
import { topbarHelpDetails } from 'app/topbar/topbar_help';
import {
    collectViewsAndCreateViewControllers,
    createGenericViewController,
} from './views/view_types/generic/view_controller_utils';
import { DynamicStatSchemaCollection } from './shared/collections/dynamic_stat_schema_collection.service';
import {
    DynamicStatSchema,
    IMS_TYPE__DYNAMIC_STAT_SCHEMA,
} from './shared/collections/dynamic_stat_schema';
import { DynamicStatsManager } from './shared/dynamic_stats_manager.service';
import { SystemTypeCollection } from './shared/collections/system_type_collection.service';
import { CensoredSystemCollection } from './shared/collections/censored_system_collection.service';
import { StoreAndForwardCRUDRunner } from 'app/shared/store_and_forward/CRUDRunner';
import { StoreAndForwarder } from 'app/shared/store_and_forward/StoreAndForwarder';
import { StoreAndForwardJobStore } from 'app/shared/store_and_forward/StoreAndForwardJobStore';
import { UserGeneratedConfigService } from 'app/config/user_generated_config.service';
import { ConfigCollection } from 'app/shared/collections/config_collection.service';
import { CesiumMap } from 'app/map/map';
import { OffsetManager } from 'app/map/geometry/cesium/OffsetManager';
import { CesiumAssetManager } from 'app/map/geometry/cesium/CesiumAssetManager';
import { calculateAngleAroundZeroZero } from 'app/shared/utilities/math_helper';
import { TimeZoneManager } from 'app/shared/time/TimeZoneManager';
import { Clock } from 'app/shared/time/clock';
import { Timeline } from 'app/shared/time/timeline';
import { GeoDataService } from 'app/map/services/heatmap/GeoDataService';
import { TimeZone } from 'app/shared/time/TimeZone';
import { FrontEndConfigFormService } from 'app/overview/config/FrontEndConfigFormService';
import { AuthorizationErrorHandler } from 'app/shared/auth/services/AuthorizationErrorHandler';
import { LazyMapOptionsHelperFactory } from 'app/overview/config/data/MapLayerOptionsHelper';
import {
    editTransmitterHelpDetails,
    onClickPanelHelpDetails,
} from './views/view_types/network/features/rf_planning/help/rf_planning_help';
import { MapOptionsService } from 'app/shared/map_options/MapOptionsService';
import { currentUrlParams } from './shared/url_and_history/url_and_history';
import { SiteStateService } from './shared/site_state.service';
import { QuickstartGuideService } from './shared/quickstart_guide/quickstart_guide.service';
import { ToastService } from './shared/toast/ToastService';
import { InitialMapOptionsFactory } from 'app/shared/map_options/InitialMapOptionsFactory';
import { CesiumZonePropertyUpdater } from 'app/map/geometry/cesium/CesiumZones/CesiumZonePropertyUpdater';
import { CesiumZoneCollection } from 'app/map/geometry/cesium/CesiumZones/CesiumZoneCollection';
import { CesiumZoneUpdater } from 'app/map/geometry/cesium/CesiumZones/CesiumZoneUpdater';
import { CesiumZonePositionSampler } from 'app/map/geometry/cesium/CesiumZones/CesiumZonePositionSampler';
import { CesiumZoneService } from './map/geometry/cesium/CesiumZones/CesiumZoneService';
import { HeatmapDataRequesterFactory } from 'app/map/services/heatmap/HeatmapDataRequesterFactory';
import { DESKTOP_ASSET_BODY } from './items/assets/asset_body/asset_body.component';
import { Interval } from 'app/shared/utilities/interval';
import { assignServicesToWindow } from 'app/bootstrap_common';
import { StartupRegistry } from 'app/startup/registry/registry';
import { last } from 'lodash';
import { TerrainLoadedStateProvider } from 'app/map/services/terrain_state_provider';
import { ColorLegendService } from 'app/shared/components/color_legend/ColorLegendService';
import IInjectorService = angular.auto.IInjectorService;
import { MapDrawingInputHandler } from 'app/map/services/map_drawing_input_handler';
import { GenericHandler } from 'app/map/services/map_input_handling/MapInputHandler';
import { MapInputHandlerCollection } from 'app/map/services/map_input_handling/map_input_handler_collection';
import { CesiumMapInputHandlerCollection } from 'app/map/services/map_input_handling/cesium_map_input_handler_collection';
import { MapInputHandlerService } from 'app/map/services/map_input_handling/MapInputHandlerService';
import { MapInputHandlerForSelections } from 'app/map/services/MapInputHandlerForSelections';
import { AssetMoverMapInputHandler } from 'app/context_menu/asset_mover/AssetMoverMapInputHandler';
import { KMLFeatureLayerCollection } from 'app/shared/collections/kml_feature_layer_collection.service';
import {
    IMS_TYPE__KML_FEATURE_LAYER,
    KMLFeatureLayer,
} from 'app/shared/collections/kml_feature_layer';
import { AgPanelService } from 'app/agriculture/entities/ag_panel_service';
import { AgricultureToastService } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { AgriculturePanesService } from './agriculture/AgriculturePanesService/AgriculturePanesService';
import { SnappingService } from 'app/agriculture/cesium/snapping/snapping_service';
import { createRoot } from 'react-dom/client';
import { Services } from 'app/items/shared/context_interface';
import { App } from 'app/App';
import { imageryLayerDefinitions } from 'imagery_layer_definitions';

standardApplicationBootstrapping();

const APP_MODULE_NAME = 'ims';

// we'll use this later to register services
let $p;

window.services = {} as Services;

const onUnauthorizedRequestEvent = new EventRegistry();
const onForbiddenRequestEvent = new EventRegistry();

const angularModule = angular
    .module(APP_MODULE_NAME, [
        localStorageModule,
        ngResourceModule,
        ngCookiesModule,
        ngStrapModule,
        ngUIBootstrapModule,
        ngUIBootstrapDateTimePicker,
        ngSanitizeModule,
        'ivh.treeview',
    ])
    .config(
        function ($provide, $resourceProvider, $locationProvider, $compileProvider, $httpProvider) {
            // extract a reference to the function that can be used to register services for later use
            $p = $provide;

            // to re-enable debug mode you can call angular.reloadWithDebugInfo() from the console
            $compileProvider.debugInfoEnabled(false);

            $resourceProvider.defaults.stripTrailingSlashes = false;

            $locationProvider.html5Mode({ enabled: true, requireBase: false, rewriteLinks: false });

            uibTabDirectiveDecorator($provide, angular);
            $httpProvider.defaults.withCredentials = true;
            $httpProvider.defaults.xsrfCookieName = 'csrftoken';
            $httpProvider.defaults.xsrfHeaderName = 'X-CSRFToken';
            $httpProvider.interceptors.push(HTTP_INTERCEPTOR_FACTORY_NAME);
        },
    )
    .constant('ImsIconUrlLookup', ImsIconUrlLookup)
    .directive('titleUpdater', titleUpdaterDirective)
    .directive('uibTabWithCount', uibTabWithCountDirective)
    .directive('autoResizeSelect', autoResizeSelectDirective)
    .directive('assetNextHopTree', assetNextHopTreeDirective)
    .directive('compassDragRing', compassDragRingDirective)
    .directive('ngRightClick', ngRightClickDirective)
    .directive('emptyToNull', emptyToNullDirective)
    .directive('onSizeChanged', onSizeChangedDirective)
    .filter('trusted', trustedFilter);

withHTTPInterceptor(angularModule, onForbiddenRequestEvent, onUnauthorizedRequestEvent);
addSharedComponents(angularModule);

angularModule.run(function (
    // angular
    $injector: IInjectorService,
    $rootScope: IRootScopeService,
    $compile: ICompileService,
    $http: IHttpService,
    $filter: IFilterService,
    $window: IWindowService,
    $timeout: ITimeoutService,
    $q: IQService,
    $interval: IIntervalService,
    // angular libraries
    localStorageService,
    $resource,
    $cookies: ICookie,
    $templateCache: ITemplateCacheService,
    $templateRequest: ITemplateRequestService,
    $location: ILocationService,
) {
    setupAngularImports($injector);
    const apiUrlRootStandard = ConfigService.apiUrlRoot;
    const apiUrlRootDashboard = apiUrlRootStandard;
    setupResources($resource, apiUrlRootStandard, apiUrlRootDashboard);

    // Angular-strap's modal doesn't allow inline templates for inner content
    $templateCache.put(
        'n_way_confirmation_modal_content.html',
        require('./shared/modal/n_way_confirmation/n_way_confirmation_modal_content.html'),
    );
    $templateCache.put(
        'test_credentials_modal_content.html',
        require('./shared/modal/test_credentials/test_credentials_modal_content.html'),
    );
    $templateCache.put(
        'import_feature_layer_modal_content.html',
        require('./shared/modal/import_feature_layer/import_feature_layer_modal_content.html'),
    );
    $templateCache.put(
        'ims_tab_with_count.html',
        require('./shared/components/tab_count/ims_tab_with_count.html'),
    );

    const authErrorResolver = AuthorizationErrorHandler.createWithDefaults(window);
    onUnauthorizedRequestEvent.addEventListener(() => authErrorResolver.onUnauthorizedRequest());
    onForbiddenRequestEvent.addEventListener(() => authErrorResolver.onForbiddenRequest());

    const stage1StartUpRegistry = new StartupRegistry('stage1');
    const stage2StartUpRegistry = new StartupRegistry('stage2');

    /**
     * @deprecated
     * This service is mocked to support legacy references. Please do not use this service.
     */
    const mainService = {
        initialStateValue: () => {},
        initialiseUrl: () => {},
    } as unknown as MainService;

    const panesService = new AgriculturePanesService($rootScope);
    const titleService = new TitleService();

    const stage1Services = {
        mainService,
        titleService,
        panesService,
        stage1StartUpRegistry,
        stage2StartUpRegistry,
    };

    stage1StartUpRegistry.startAll();

    assignServicesToWindow($p, stage1Services);

    // After the licensing collection is ready, continue to the rest of the init process
    stage1StartUpRegistry.waitForAllReady().then(() => {
        // STAGE 2 initialisation
        const initParams = new UrlInitParams(currentUrlParams(window), new Cartesian3Util(Cesium));

        const restrictionsService = new AgricultureRestrictionsService();
        const imsPanelService = new ImsPanelService(localStorageService, []);

        const timeZoneManager = new TimeZoneManager(TimeZone.LOCAL);
        const helpRegistry = new HelpRegistry();

        const colorRendererService = new ColorRendererService(ConfigService);
        const quickstartGuideService = new QuickstartGuideService();

        const imsItemTypeRegistry = new ImsItemTypeRegistry(helpRegistry, quickstartGuideService);

        const storeAndForwardJobStore = new StoreAndForwardJobStore(
            imsItemTypeRegistry,
            window.localStorage,
        );
        const crudRunner = new StoreAndForwardCRUDRunner({
            jobStore: storeAndForwardJobStore,
            imsItemTypeRegistry: imsItemTypeRegistry,
            navigator: window.navigator,
        });
        const storeAndForwarder = new StoreAndForwarder(storeAndForwardJobStore, crudRunner, 3000);

        let defaultCollectionOptions = {};
        if (ConfigService.enableStoreAndForward) {
            // Set the default collection options to include a non-default CRUDRunner.
            // You'll note that not many collections use these special options at the moment.
            defaultCollectionOptions = {
                crudRunner: crudRunner,
            };
            // Start the store and forwarder which periodically attempts to complete
            // store and forward jobs.
            // In theory this should never finish
            storeAndForwarder.start().finally(() => {
                console.log('Store and forwarder shut down!');
            });
        }

        const viewCollection = new ViewCollection(
            stage2StartUpRegistry,
            localStorageService,
            $interval,
        );

        const assetTypeCollection = new AssetTypeCollection(
            stage2StartUpRegistry,
            localStorageService,
            $interval,
        );

        const htmlDomDocumentService = new HTMLDomDocumentService();
        const serverTimeOffsetService = new ServerTimeOffsetService(
            new Interval(),
            ConfigService,
            $http,
            () => new Date(),
            htmlDomDocumentService,
        );

        const viewStateService = new ViewStateService(
            initParams,
            localStorageService,
            titleService,
            viewCollection,
            mainService,
            ConfigService,
        );

        const siteStateService = new SiteStateService();

        const configCollection = new ConfigCollection(
            stage2StartUpRegistry,
            localStorageService,
            $rootScope,
            $interval,
            viewStateService,
            siteStateService,
        );

        const imageryMapOptionsHelperFactory = new LazyMapOptionsHelperFactory();

        const userGeneratedConfigService = new UserGeneratedConfigService(
            configCollection,
            imageryMapOptionsHelperFactory,
            ConfigService,
        );

        const frontEndConfigFormService = new FrontEndConfigFormService(userGeneratedConfigService);

        const cesiumClock = new Cesium.Clock();

        const clock = new Clock(cesiumClock, $window);

        async function loadConfig() {
            const imageryLayerMapOptionDef = imageryLayerDefinitions;
            imageryMapOptionsHelperFactory.setMapOptionsDef(imageryLayerMapOptionDef);
            frontEndConfigFormService.setImageryMapOptionsHelper(
                imageryMapOptionsHelperFactory.createHelper(),
            );
            await userGeneratedConfigService.init();
        }

        void loadConfig();
        const kmlFeatureLayerCollection = new KMLFeatureLayerCollection(
            stage2StartUpRegistry,
            localStorageService,
            $rootScope,
            $interval,
            viewStateService,
            siteStateService,
        );

        const terrainService = new TerrainService(
            $rootScope,
            localStorageService,
            userGeneratedConfigService,
            initParams,
            ConfigService,
        );

        const cesiumMap = new CesiumMap(ConfigService);

        const imsImageryLayersService = new ImsImageryLayersService(cesiumMap.viewer);
        const cesiumTimeline = cesiumMap.viewer.timeline;

        const timeline = new Timeline(
            cesiumTimeline as any,
            panesService,
            $rootScope,
            timeZoneManager,
        );

        const clockHandler = new ClockHandler(
            new ClockHandlerFSM(),
            $rootScope,
            userGeneratedConfigService,
            serverTimeOffsetService,
            clock,
            timeline,
            initParams,
            ConfigService.timelineMinutes,
            ConfigService.streamMargin,
        );

        const tagCollection = new TagCollection(
            stage2StartUpRegistry,
            localStorageService,
            $rootScope,
            $interval,
            viewStateService,
            siteStateService,
        );

        const systemTypeCollection = new SystemTypeCollection(
            stage2StartUpRegistry,
            localStorageService,
            $interval,
        );

        const censoredSystemCollection = new CensoredSystemCollection(
            stage2StartUpRegistry,
            localStorageService,
            $interval,
            siteStateService,
            systemTypeCollection,
        );

        const integrationCollection = new IntegrationCollection(
            stage2StartUpRegistry,
            localStorageService,
            $rootScope,
            $interval,
            viewStateService,
            siteStateService,
            censoredSystemCollection,
        );

        const terrainLoadedStateProvider = new TerrainLoadedStateProvider(terrainService);
        const locationProcessor = new LocationProcessor(
            terrainService,
            ConfigService.locationInterpolationCutoffSeconds,
            terrainLoadedStateProvider,
            (invalidStats) => {
                console.warn(
                    `Found ${invalidStats.length} invalid location stats (likely insane lat/lon values). Last invalid location stat found:`,
                    last(invalidStats),
                );
            },
        );

        const assetCollection = new AssetCollection(
            stage2StartUpRegistry,
            localStorageService,
            $rootScope,
            $interval,
            viewStateService,
            siteStateService,
            assetTypeCollection,
            tagCollection,
            integrationCollection,
            Object.assign({}, defaultCollectionOptions),
        );

        const mapService = new MapService(
            initParams,
            $rootScope,
            $q,
            terrainService,
            cesiumMap,
            userGeneratedConfigService,
            ConfigService,
        );

        const geoDataService = new GeoDataService(
            mapService,
            viewStateService,
            $window,
            imsImageryLayersService,
            {},
            new HeatmapDataRequesterFactory(userGeneratedConfigService),
            ConfigService,
        );

        const selectionService = new SelectionService(
            mainService,
            kmlFeatureLayerCollection,
            imsItemTypeRegistry,
            geoDataService,
        );

        const zoneTypeCollection = new ZoneTypeCollection(
            stage2StartUpRegistry,
            localStorageService,
            $interval,
        );
        const zoneHistoryCollection = new ZoneHistoryCollection(
            stage2StartUpRegistry,
            localStorageService,
            $rootScope,
            $interval,
            viewStateService,
            siteStateService,
        );
        const zoneCollection = new ZoneCollection(
            stage2StartUpRegistry,
            localStorageService,
            $rootScope,
            $interval,
            zoneTypeCollection,
            zoneHistoryCollection,
            viewStateService,
            siteStateService,
        );

        const locationStatCollection = new LocationStatCollection(
            stage2StartUpRegistry,
            localStorageService,
            $rootScope,
            $timeout,
            $interval,
            clockHandler,
            viewStateService,
            siteStateService,
            locationProcessor,
            assetCollection,
            Object.assign({}, defaultCollectionOptions),
        );

        assetCollection.setLocationStatCollection(locationStatCollection);

        if (ConfigService.enableStoreAndForward) {
            storeAndForwardJobStore.addCollectionToWatch(assetCollection);
        }

        const dynamicStatSchemaCollection = new DynamicStatSchemaCollection(
            stage2StartUpRegistry,
            localStorageService,
            $interval,
            userGeneratedConfigService,
        );

        imsItemTypeRegistry.setDynamicStatSchemaCollection(dynamicStatSchemaCollection);

        const imsObjectVisibilityService = new ImsObjectVisibilityService(
            mainService,
            assetCollection,
            zoneCollection,
        );

        const colorLegendService = new ColorLegendService(imsPanelService);

        imsItemTypeRegistry.register(IMS_TYPE__ASSET, {
            displayName: 'Asset',
            collection: assetCollection,
            ItemClass: Asset,
            panelDetails: {
                inUrl: true,
                stateKey: 'assets',
                bodyComponentName: DESKTOP_ASSET_BODY,
                configOverrides: {
                    detachedMinWidth: 400,
                    detachedMinHeight: 450,
                    detachedWidth: 400,
                    detachedHeight: 450,
                    resizable: true,
                },
            },
            helpDetails: assetHelpDetails,
            toFilterableItem: Asset.toFilterable,
        });

        imsItemTypeRegistry.register(IMS_TYPE__ASSET_TYPE, {
            displayName: 'Asset Type',
            collection: assetTypeCollection,
            ItemClass: AssetType,
            toFilterableItem: AssetType.toFilterable,
        });

        imsItemTypeRegistry.register(IMS_TYPE__INTEGRATION, {
            displayName: 'Integration',
            collection: integrationCollection,
            ItemClass: Integration,
        });

        imsItemTypeRegistry.register(IMS_TYPE__LOCATION_STAT, {
            displayName: 'Location Stat',
            collection: locationStatCollection,
            ItemClass: LocationStats,
            isStat: true,
            statDefinition: locationStatDefinition,
        });

        imsItemTypeRegistry.register(IMS_TYPE__CENSORED_SYSTEM, {
            displayName: 'Censored System',
            collection: censoredSystemCollection,
            ItemClass: CensoredSystem,
        });

        imsItemTypeRegistry.register(IMS_TYPE__TAG, {
            displayName: 'Tag',
            collection: tagCollection,
            ItemClass: Tag,
        });

        // imsItemTypeRegistry.register(IMS_TYPE__USER, {
        //     displayName: 'User',
        //     collection: userCollection,
        //     ItemClass: User,
        //     panelDetails: {
        //         inUrl: true,
        //         stateKey: 'users',
        //         bodyComponentName: userBodyComponent.name
        //     }
        // });

        imsItemTypeRegistry.register(IMS_TYPE__VIEW, {
            displayName: 'View',
            collection: viewCollection,
            ItemClass: View,
        });

        imsItemTypeRegistry.register(IMS_TYPE__KML_FEATURE_LAYER, {
            displayName: 'Feature Layer',
            collection: kmlFeatureLayerCollection,
            ItemClass: KMLFeatureLayer,
        });

        imsItemTypeRegistry.register(IMS_TYPE__ZONE_HISTORY, {
            displayName: 'Zone History Point',
            collection: zoneHistoryCollection,
            ItemClass: ZoneHistory,
        });

        imsItemTypeRegistry.register(IMS_TYPE__ZONE_TYPE, {
            displayName: 'Zone Type',
            collection: zoneTypeCollection,
            ItemClass: ZoneType,
        });

        imsItemTypeRegistry.register(IMS_TYPE__DYNAMIC_STAT_SCHEMA, {
            displayName: 'DynamicStatSchema',
            collection: dynamicStatSchemaCollection,
            ItemClass: DynamicStatSchema,
        });

        const dynamicStatsManager = new DynamicStatsManager(
            stage2StartUpRegistry,
            localStorageService,
            imsItemTypeRegistry,
            dynamicStatSchemaCollection,
            $resource,
            $rootScope,
            $timeout,
            $interval,
            clockHandler,
            viewStateService,
            siteStateService,
            apiUrlRootStandard,
        );

        const imsItemPanelService = new ImsItemPanelService(
            mainService,
            imsPanelService,
            imsItemTypeRegistry,
        );

        if (ConfigService.enableStoreAndForward) {
            imsItemPanelService.addCollectionToWatch(assetCollection);
        }

        const keypressService = new KeypressService();

        const initialMapOptionsSetterFactory = new InitialMapOptionsFactory(localStorageService);

        const assetOptionsService = new MapOptionsService(
            viewStateService,
            viewCollection,
            localStorageService,
            'current_map_options', // This key is used for backward compatibility reasons
            initialMapOptionsSetterFactory,
            colorLegendService,
        );

        const zoneOptionsService = new MapOptionsService(
            viewStateService,
            viewCollection,
            localStorageService,
            'current_map_zone_options', // This key is used for backward compatibility reasons
            initialMapOptionsSetterFactory,
            colorLegendService,
        );

        const imageUrlService = new ImageUrlService($location, assetTypeCollection);

        helpRegistry.register(manageDashboardTabHelpDetails);
        helpRegistry.register(dashboardDataSourceSettingsHelp);
        helpRegistry.register(dashboardLegendHelp);
        helpRegistry.register(dashboardItemHelp);
        helpRegistry.register(topbarHelpDetails);
        helpRegistry.register(createAssetTour);
        helpRegistry.register(createIpDeviceTour);
        helpRegistry.register(onClickPanelHelpDetails);
        helpRegistry.register(editTransmitterHelpDetails);

        const toastService = new ToastService();

        // don't delete this yet, it has many side-effects that will break the map if not run
        // can only be removed safely when the map service is re-written
        const baseImageryLayersService = new BaseImageryLayersService(
            $rootScope,
            localStorageService,
            imsImageryLayersService,
            userGeneratedConfigService,
        );

        const dashboardItemService = new DashboardItemService(
            dynamicStatSchemaCollection,
            imsItemTypeRegistry,
            $resource,
            colorRendererService,
            apiUrlRootDashboard,
            userGeneratedConfigService,
            restrictionsService,
            ConfigService,
        );

        const cesiumOffsetManager = new OffsetManager();

        /* This should be a seperate map builder */
        const cesiumAssetManager = new CesiumAssetManager(
            cesiumMap,
            clockHandler,
            assetCollection,
            zoneCollection,
            locationStatCollection,
            viewStateService,
            siteStateService,
            imageUrlService,
            terrainService,
            selectionService,
            imsObjectVisibilityService,
            assetOptionsService,
            cesiumOffsetManager,
            imsItemTypeRegistry,
            assetTypeCollection,
            ConfigService,
        );

        const cesiumZoneCollection = new CesiumZoneCollection();

        const cesiumZoneLabelHeightSampler = new CesiumZonePositionSampler(
            zoneCollection,
            clockHandler,
            terrainService,
        );

        const cesiumZonePropertyUpdater = new CesiumZonePropertyUpdater(
            zoneOptionsService,
            viewStateService,
            terrainService,
            zoneCollection,
            cesiumZoneCollection,
            cesiumZoneLabelHeightSampler,
            cesiumOffsetManager,
            terrainLoadedStateProvider,
        );

        const cesiumZoneUpdater = new CesiumZoneUpdater(
            zoneCollection,
            cesiumZoneCollection,
            siteStateService,
            zoneOptionsService,
            imsObjectVisibilityService,
            selectionService,
            imsItemTypeRegistry,
            clockHandler,
            cesiumZonePropertyUpdater,
            ConfigService.zones,
        );

        const cesiumZoneService = new CesiumZoneService(
            zoneCollection,
            assetCollection,
            clockHandler,
            cesiumMap,
            selectionService,
            ConfigService.zones, // Boolean indicating whether or not zones should be enabled
            siteStateService,
            toastService,
            restrictionsService,
            new Cesium.LabelCollection(),
            cesiumZonePropertyUpdater,
            cesiumZoneUpdater,
            cesiumZoneCollection,
            cesiumOffsetManager,
        );

        /*****************************************/

        const mapSitesService = new MapSitesService(mapService, imageUrlService);

        const rfTransmitterIconCollection = new Cesium.BillboardCollection();
        const selectionCollection = new Cesium.BillboardCollection();
        const labelCollection = new Cesium.LabelCollection();
        const polylineCollection = new Cesium.PolylineCollection();

        cesiumMap.viewer.scene.primitives.add(rfTransmitterIconCollection);
        cesiumMap.viewer.scene.primitives.add(selectionCollection);
        cesiumMap.viewer.scene.primitives.add(labelCollection);
        cesiumMap.viewer.scene.primitives.add(polylineCollection);

        const agPanelService = new AgPanelService();

        const mapSelectionHandler = new MapInputHandlerForSelections(
            selectionService,
            keypressService,
            siteStateService,
            mapSitesService,
            cesiumZoneService,
            cesiumAssetManager,
            mapService,
            geoDataService,
            panesService,
            $rootScope,
        );
        mapSelectionHandler.isDefault = true;

        const mapInputHandlerCollection = new MapInputHandlerCollection();
        const cesiumMapInputHandlerCollection = new CesiumMapInputHandlerCollection();

        const mapInputHandlerService = new MapInputHandlerService(
            mapService,
            mapInputHandlerCollection,
            cesiumMapInputHandlerCollection,
        );

        const assetMoverMapInputHandler = new AssetMoverMapInputHandler(mapService);

        const noopHandler = new GenericHandler();
        noopHandler.name = 'noopHandler';

        mapInputHandlerService.addMapInputHandler(noopHandler);
        mapInputHandlerService.addMapInputHandler(mapSelectionHandler);
        mapInputHandlerService.addMapInputHandler(assetMoverMapInputHandler);

        const mapTooltipService = new MapTooltipService(mapService);

        const mapDrawingInputHandler = new MapDrawingInputHandler(
            mapInputHandlerService,
            clockHandler,
            mapTooltipService,
            selectionService,
            mapService,
        );

        mapInputHandlerService.addMapInputHandler(mapDrawingInputHandler);

        collectViewsAndCreateViewControllers(
            viewCollection,
            viewStateService,
            imsPanelService,
            dynamicStatsManager,
            (view: View) =>
                createGenericViewController(
                    view,
                    assetOptionsService,
                    zoneOptionsService,
                    imsItemTypeRegistry,
                    imageUrlService,
                    dashboardItemService,
                    assetCollection,
                    zoneCollection,
                    clockHandler,
                    imsObjectVisibilityService,
                    selectionService,
                ),
        );

        const compass = new Compass(calculateAngleAroundZeroZero);

        const scaleIndicator = new ScaleIndicator(
            [
                0.1, 0.5, 1, 5, 10, 20, 50, 100, 250, 500, 1000, 2000, 5000, 10000, 20000, 50000,
                100000, 250000, 500000, 1000000, 2500000, 5000000, 10000000, 20000000,
            ],
            100,
        );

        // TODO es6, this kicks everything off
        // $rootScope.$on(CESIUM_LOAD_EVENT, () => {
        //     mapService.initialise();
        // });

        // TODO: Should be done as part of the map directives construction
        mapService.getViewer().then((viewer) => {
            scaleIndicator.initialise(viewer);
            compass.initialise(viewer);
        });

        const agricultureToastService = new AgricultureToastService();

        const apiSyncService = new ApiSyncService(storeAndForwardJobStore);

        const snappingService = new SnappingService();

        /*
     only need to provide services that are required by components
     as they're the only part still using Angular's dependency injection
     Object  { serviceName: serviceInstance }
     OR      { serviceNameAndInstanceName } for short (ordering doesn't matter)
     */
        const stage2Services = {
            // services used in ag front end
            agPanelService,
            agricultureToastService,
            assetCollection,
            assetTypeCollection,
            cesiumMap,
            imsObjectVisibilityService,
            mapInputHandlerService,
            mapService,
            siteStateService,
            scaleIndicator,
            selectionService,
            snappingService,
            stage2StartUpRegistry,
            terrainService,

            // other stuff that is maybe used, who knows :|
            // $rootScope,
            // $compile,
            // $timeout,
            // userGeneratedConfigService,
            // moment,
            // $http,
            // $filter,
            // $templateRequest,
            // imsqlService,
            // ipAddressAndHostRegistryService,
            // triggerResultsService,
            // imsItemPanelService,
            // tourService,
            // flyToService,
            // imsItemTypeRegistry,
            // helpPanelService,
            // helpSearchService,
            // centerPanelService,
            // helpRegistry,
            // apiSyncService,
            // topbarService,
            // userLocationService,
            // networkStatusService,
            // bulkEditService,
            // commentCountService,
            // overviewService,
            // browserCheckService,
            // dragDataStoreService,
            // zoneCollection,
            // kmlFeatureLayerCollection,
            // zoneTypeCollection,
            // locationStatCollection,
            // colorRendererService,
            // zoneHistoryCollection,
            // cesiumOffsetManager,
            // titleService,
            // integrationCollection,
            // imsPanelService,
            // dropdownService,
            // tagCollection,
            // dashboardItemService,
            // zoneService,
            // kmzMaker,
            // viewStateService,
            // assetOptionsService,
            // baseImageryLayersService,
            // ConfigService,
            // ticketCountService,
            // compass,
            // baseInfoBoxService,
            // mapToolboxService,
            // homeButtonTool,
            // mapDrawingInputHandler,
            // mapTooltipService,
            // viewCollection,
            // heatMapOptions,
            // infoBoxService,
            // formService,
            // mapMarkerService,
            // imageUrlService,
            // chartColorMap,
            // tableResizeEventRegistry,
            // managePageService,
            // zoneQuicklistService,
            // assetQuicklistService,
            // triggerSnoozeService,
            // dynamicStatSchemaCollection,
            // mapTrackingService,
            // storeAndForwarder,
            // storeAndForwardJobStore,
            // storeAndForwardService,
            // systemTypeCollection,
            // censoredSystemCollection,
            // systemsConfigService,
            // assetLocationService,
            // assetMoverService,
            // geoDataService,
            // timeZoneManager,
            // timeZoneMediator,
            // imsImageryLayersService,
            // cesiumGeoDataPointAssetManager,
            // frontEndConfigFormService,
            // localStorageService,
            // cesiumZoneService,
            // zoneOptionsService,
            // imsDataVisLinkService,
            // quickstartGuideService,
            // dashboardItemPostAggHelpers,
            // statFormatterForInfoBox,
            // restrictionsService,
            // serverTimeOffsetService,
            // unitService,
            // imageViewerService,
            // toastService,
            // assetPanelService,
            // qrCodeScannerService,
            // imsItemPanelDesktop,
            // ipDevicePanelService,
            // configCollection,
            // terrainLoadedStateProvider,
            // colorLegendService,
            // mapInputHandlerCollection,
            // infoBoxHeatMapService,
            // featureLayersService,
            // mapSitesService,
            // just put the window in here anyway to make it reachable through DI rather than the global
            window: window,
        };

        assignServicesToWindow($p, stage2Services);

        /**
         * If we are in the middle of a sync, warn the user. We may want to hook in other forms
         * into this check
         */
        window.onload = function () {
            window.addEventListener('beforeunload', function (e) {
                if (!apiSyncService.isSyncing()) {
                    return undefined;
                }
                // Chrome shows its own confirmation message so I guess this text is
                // only used on other browsers
                const confirmationMessage =
                    'Do you want to leave the farm?' + 'Changes that you made may not be saved';

                (e || window.event).returnValue = confirmationMessage; //Gecko + IE
                return confirmationMessage; //Gecko + Webkit, Safari, Chrome etc.
            });
        };

        // This needs to be after any services with a state provider linked to mainService.state
        // so that any state keys tagged with URL have already been initialised.
        mainService.initialiseUrl();
    });
});

angular.bootstrap(document, [APP_MODULE_NAME]);

// **** REACT SETUP ****
const root = createRoot(document.getElementById('agriculture-main'));
root.render(<App />);
//
